/**
 * use-sidecar-run
 *
 * Tracks live and recent Sidecar runs for the active chat.
 *
 * Primary responsibilities:
 * - Listen to Sidecar server events
 * - Build live run and worker state for the fixed Sidecar panel
 * - Keep recent runs available for switching after completion
 * - Keep Sidecar tool timelines isolated from the parent chat message
 *
 * Dependencies:
 * - hooks/useServerEvents
 * - types/sidecar
 */

'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useServerEvents } from '@/hooks/useServerEvents'
import { buildSeedRunFromAnnotation } from '@/lib/sidecar-seed'
import type {
  SidecarAgentAnnotationEventData,
  SidecarAgentEventData,
  SidecarAgentState,
  SidecarAgentToolEventData,
  SidecarRunEventData,
  SidecarRunState
} from '@/types/sidecar'

interface UseSidecarRunOptions {
  chatId?: string | null
  enabled?: boolean
  seedRuns?: SidecarRunState[]
}

interface UseSidecarRunResult {
  currentRun: SidecarRunState | null
  latestRun: SidecarRunState | null
  runs: SidecarRunState[]
  selectRun: (runId: string, options?: { manual?: boolean }) => void
  clearRun: () => void
}

type RunSelectionMode = 'auto' | 'manual'
const MAX_PERSISTED_RUNS = 8

function isRealSidecarRunId(runId: string): boolean {
  return runId.startsWith('sidecar-')
}

function getScopedEventChatId(value: unknown): string | null {
  if (typeof value !== 'string') return null

  const chatId = value.trim()
  return chatId || null
}

function getRunStatusRank(status: SidecarRunState['status']): number {
  if (status === 'completed') return 4
  if (status === 'cancelled') return 4
  if (status === 'error') return 3
  if (status === 'running') return 2
  return 1
}

function getAgentStatusRank(status: SidecarAgentState['status']): number {
  if (status === 'completed') return 4
  if (status === 'error') return 4
  if (status === 'running') return 3
  return 1
}

function createRunStateFromRunEvent(payload: SidecarRunEventData): SidecarRunState {
  return {
    runId: payload.runId,
    chatId: payload.chatId,
    status: payload.status,
    executionMode: payload.executionMode,
    objective: payload.objective,
    reason: payload.reason,
    message: payload.message,
    agentCount: payload.agentCount,
    plannedWorkers: payload.plannedWorkers,
    successfulAgents: payload.successfulAgents,
    failedAgents: payload.failedAgents,
    finalAnswer: payload.finalAnswer,
    usedDeterministicFallback: payload.usedDeterministicFallback,
    startedAt: payload.startedAt ?? Date.now(),
    completedAt: payload.completedAt,
    agentOrder: [],
    agents: {}
  }
}

function createRunStateFromAgentEvent(payload: SidecarAgentEventData): SidecarRunState {
  return {
    runId: payload.runId,
    chatId: payload.chatId,
    status: payload.status === 'error' ? 'error' : 'running',
    startedAt: payload.startedAt ?? Date.now(),
    plannedWorkers: [],
    agentOrder: [],
    agents: {}
  }
}

function createPlaceholderRunState(
  chatId: string,
  runId: string
): SidecarRunState {
  return {
    runId,
    chatId,
    status: 'running',
    startedAt: Date.now(),
    agentOrder: [],
    agents: {}
  }
}

function readStartObjective(request: unknown): string | undefined {
  if (!request || typeof request !== 'object' || Array.isArray(request)) {
    return undefined
  }

  const record = request as Record<string, unknown>
  if (typeof record.objective === 'string' && record.objective.trim()) {
    return record.objective.trim()
  }

  const parameters =
    record.parameters &&
    typeof record.parameters === 'object' &&
    !Array.isArray(record.parameters)
      ? (record.parameters as Record<string, unknown>)
      : null

  return typeof parameters?.objective === 'string' && parameters.objective.trim()
    ? parameters.objective.trim()
    : undefined
}

function createPlaceholderAgentState(
  payload:
    | SidecarAgentToolEventData
    | SidecarAgentAnnotationEventData
): SidecarAgentState {
  return {
    id: payload.agentId,
    title: 'Helper',
    objective: 'Working on a delegated task',
    status: 'running',
    toolTimeline: [],
    annotations: {}
  }
}

function mergeRunEvent(
  run: SidecarRunState,
  payload: SidecarRunEventData
): SidecarRunState {
  const status =
    getRunStatusRank(run.status) > getRunStatusRank(payload.status)
      ? run.status
      : payload.status

  return {
    ...run,
    status,
    executionMode: payload.executionMode ?? run.executionMode,
    objective: payload.objective ?? run.objective,
    reason: payload.reason ?? run.reason,
    message: payload.message ?? run.message,
    agentCount: payload.agentCount ?? run.agentCount,
    plannedWorkers: payload.plannedWorkers ?? run.plannedWorkers,
    successfulAgents: payload.successfulAgents ?? run.successfulAgents,
    failedAgents: payload.failedAgents ?? run.failedAgents,
    finalAnswer:
      status === 'completed'
        ? payload.finalAnswer ?? run.finalAnswer
        : run.finalAnswer ?? payload.finalAnswer,
    usedDeterministicFallback:
      payload.usedDeterministicFallback ?? run.usedDeterministicFallback,
    startedAt: payload.startedAt ?? run.startedAt,
    completedAt:
      payload.completedAt && run.completedAt
        ? Math.max(payload.completedAt, run.completedAt)
        : payload.completedAt ?? run.completedAt
  }
}

function sortRuns(a: SidecarRunState, b: SidecarRunState): number {
  return b.startedAt - a.startedAt
}

function upsertAgent(run: SidecarRunState, payload: SidecarAgentEventData): SidecarRunState {
  const existing = run.agents[payload.agentId]
  const plannedWorker = run.plannedWorkers?.find((worker) => worker.id === payload.agentId)
  const nextStatus = payload.status
  const currentTool =
    payload.currentTool !== undefined
      ? payload.currentTool
      : nextStatus === 'completed' || nextStatus === 'error'
        ? undefined
        : existing?.currentTool

  const nextAgent: SidecarAgentState = {
    id: payload.agentId,
    title: payload.title,
    objective: payload.objective,
    source: payload.source ?? plannedWorker?.source ?? existing?.source ?? 'ad_hoc',
    helperTemplateId:
      payload.helperTemplateId ?? plannedWorker?.helperTemplateId ?? existing?.helperTemplateId,
    helperId: payload.helperId ?? plannedWorker?.helperId ?? existing?.helperId,
    deliverable: payload.deliverable,
    status: nextStatus,
    message: payload.message ?? existing?.message,
    latestText: payload.latestText ?? existing?.latestText,
    finalOutput: payload.finalOutput ?? existing?.finalOutput,
    error: payload.error ?? existing?.error,
    currentTool,
    startedAt: payload.startedAt ?? existing?.startedAt,
    completedAt: payload.completedAt ?? existing?.completedAt,
    toolTimeline: existing?.toolTimeline ?? [],
    annotations: existing?.annotations ?? {}
  }

  return {
    ...run,
    agentCount: Math.max(run.agentCount ?? 0, Object.keys(run.agents).length + (existing ? 0 : 1)),
    agentOrder: run.agentOrder.includes(payload.agentId)
      ? run.agentOrder
      : [...run.agentOrder, payload.agentId],
    agents: {
      ...run.agents,
      [payload.agentId]: nextAgent
    }
  }
}

function upsertToolTimeline(
  run: SidecarRunState,
  payload: SidecarAgentToolEventData
): SidecarRunState {
  const agent = run.agents[payload.agentId]
  if (!agent) return run

  const agentIsTerminal =
    agent.status === 'completed' || agent.status === 'error'
  const incomingIsTerminal =
    payload.status === 'complete' || payload.status === 'error'

  if (agentIsTerminal && !incomingIsTerminal) {
    return run
  }

  const timeline = [...agent.toolTimeline]
  const existingIndex = timeline.findIndex((entry) => entry.id === payload.toolId)
  const existing = existingIndex >= 0 ? timeline[existingIndex] : null
  const existingIsTerminal =
    existing?.status === 'complete' || existing?.status === 'error'

  if (existingIsTerminal && !incomingIsTerminal) {
    return run
  }

  const nextEntry = {
    id: payload.toolId,
    name: payload.toolName,
    status: payload.status,
    sequence: payload.sequence,
    startedAt: existing?.startedAt ?? Date.now(),
    endedAt:
      payload.status === 'complete' || payload.status === 'error'
        ? Date.now()
        : existing?.endedAt,
    request: payload.request ?? existing?.request,
    result: payload.result ?? existing?.result,
    error: payload.error ?? existing?.error,
    progress: payload.progress ?? existing?.progress,
    isExpanded: existing?.isExpanded ?? payload.status === 'error'
  }

  if (existingIndex >= 0) {
    timeline[existingIndex] = {
      ...existing,
      ...nextEntry
    }
  } else {
    timeline.push(nextEntry)
  }

  timeline.sort((a, b) => a.sequence - b.sequence)

  return {
    ...run,
    agents: {
      ...run.agents,
      [payload.agentId]: {
        ...agent,
        currentTool:
          payload.status === 'complete' || payload.status === 'error' || agentIsTerminal
            ? undefined
            : payload.toolName,
        toolTimeline: timeline
      }
    }
  }
}

function mergeAnnotations(
  run: SidecarRunState,
  payload: SidecarAgentAnnotationEventData
): SidecarRunState {
  const agent = run.agents[payload.agentId]
  if (!agent) return run

  return {
    ...run,
    agents: {
      ...run.agents,
      [payload.agentId]: {
        ...agent,
        annotations: {
          ...agent.annotations,
          ...payload.annotation
        }
      }
    }
  }
}

function mergeAgentState(
  current: SidecarAgentState,
  incoming: SidecarAgentState
): SidecarAgentState {
  const status =
    getAgentStatusRank(current.status) > getAgentStatusRank(incoming.status)
      ? current.status
      : incoming.status
  const currentTool =
    incoming.currentTool !== undefined
      ? incoming.currentTool
      : status === 'completed' || status === 'error'
        ? undefined
        : current.currentTool
  const toolTimeline =
    incoming.toolTimeline.length >= current.toolTimeline.length
      ? incoming.toolTimeline
      : current.toolTimeline

  return {
    ...current,
    ...incoming,
    status,
    message: incoming.message ?? current.message,
    latestText: incoming.latestText ?? current.latestText,
    finalOutput: incoming.finalOutput ?? current.finalOutput,
    error: incoming.error ?? current.error,
    currentTool,
    startedAt: incoming.startedAt ?? current.startedAt,
    completedAt: incoming.completedAt ?? current.completedAt,
    toolTimeline,
    annotations: {
      ...current.annotations,
      ...incoming.annotations
    }
  }
}

function mergeRunState(
  current: SidecarRunState,
  incoming: SidecarRunState
): SidecarRunState {
  const mergedAgents: Record<string, SidecarAgentState> = {
    ...current.agents
  }

  for (const agentId of Object.keys(incoming.agents)) {
    const incomingAgent = incoming.agents[agentId]
    const currentAgent = mergedAgents[agentId]

    mergedAgents[agentId] = currentAgent
      ? mergeAgentState(currentAgent, incomingAgent)
      : incomingAgent
  }

  const incomingStatus =
    getRunStatusRank(current.status) > getRunStatusRank(incoming.status)
      ? current.status
      : incoming.status
  const startedAt =
    typeof current.startedAt === 'number' && typeof incoming.startedAt === 'number'
      ? Math.min(current.startedAt, incoming.startedAt)
      : incoming.startedAt ?? current.startedAt
  const completedAt =
    current.completedAt && incoming.completedAt
      ? Math.max(current.completedAt, incoming.completedAt)
      : incoming.completedAt ?? current.completedAt
  const finalAnswer =
    incomingStatus === 'completed'
      ? incoming.finalAnswer ?? current.finalAnswer
      : current.finalAnswer ?? incoming.finalAnswer

  return {
    ...current,
    ...incoming,
    status: incomingStatus,
    executionMode: incoming.executionMode ?? current.executionMode,
    objective: incoming.objective ?? current.objective,
    reason: incoming.reason ?? current.reason,
    message: incoming.message ?? current.message,
    agentCount: incoming.agentCount ?? current.agentCount,
    plannedWorkers:
      incoming.plannedWorkers && incoming.plannedWorkers.length > 0
        ? incoming.plannedWorkers
        : current.plannedWorkers,
    successfulAgents: incoming.successfulAgents ?? current.successfulAgents,
    failedAgents: incoming.failedAgents ?? current.failedAgents,
    finalAnswer,
    usedDeterministicFallback:
      incoming.usedDeterministicFallback ?? current.usedDeterministicFallback,
    startedAt,
    completedAt,
    agentOrder: Array.from(new Set([...current.agentOrder, ...incoming.agentOrder])),
    agents: mergedAgents
  }
}

function pruneRuns(runsById: Record<string, SidecarRunState>): Record<string, SidecarRunState> {
  return Object.fromEntries(
    Object.values(runsById)
      .filter((run) => isRealSidecarRunId(run.runId))
      .sort(sortRuns)
      .slice(0, MAX_PERSISTED_RUNS)
      .map((run) => [run.runId, run])
  )
}

function mergeRunCollection(
  current: Record<string, SidecarRunState>,
  incomingRuns: SidecarRunState[]
): Record<string, SidecarRunState> {
  const next = { ...current }

  for (const incoming of incomingRuns) {
    const existing = next[incoming.runId]
    next[incoming.runId] = existing ? mergeRunState(existing, incoming) : incoming
  }

  return pruneRuns(next)
}

function getStorageKey(chatId: string): string {
  return `losi:sidecar:runs:${chatId}`
}

function isStoredAgentState(value: unknown): value is SidecarAgentState {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false

  const record = value as Record<string, unknown>
  return (
    typeof record.id === 'string' &&
    typeof record.title === 'string' &&
    typeof record.objective === 'string' &&
    Array.isArray(record.toolTimeline) &&
    record.annotations != null &&
    typeof record.annotations === 'object' &&
    !Array.isArray(record.annotations)
  )
}

function isStoredRunState(value: unknown): value is SidecarRunState {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false

  const record = value as Record<string, unknown>
  if (
    typeof record.runId !== 'string' ||
    typeof record.chatId !== 'string' ||
    typeof record.startedAt !== 'number' ||
    !Array.isArray(record.agentOrder) ||
    !record.agents ||
    typeof record.agents !== 'object' ||
    Array.isArray(record.agents)
  ) {
    return false
  }

  return Object.values(record.agents).every(isStoredAgentState)
}

function readStoredRuns(chatId: string): SidecarRunState[] {
  if (typeof window === 'undefined') return []

  try {
    const raw = sessionStorage.getItem(getStorageKey(chatId))
    if (!raw) return []

    const parsed = JSON.parse(raw) as unknown
    return Array.isArray(parsed)
      ? parsed.filter(
          (run): run is SidecarRunState =>
            isStoredRunState(run) && isRealSidecarRunId(run.runId)
        )
      : []
  } catch {
    return []
  }
}

export function useSidecarRun({
  chatId,
  enabled = false,
  seedRuns = []
}: UseSidecarRunOptions): UseSidecarRunResult {
  const [runsById, setRunsById] = useState<Record<string, SidecarRunState>>({})
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null)
  const [selectionMode, setSelectionMode] = useState<RunSelectionMode>('auto')
  const { addEventListener, removeEventListener } = useServerEvents()

  useEffect(() => {
    if (!enabled || !chatId) {
      setRunsById({})
      setSelectedRunId(null)
      setSelectionMode('auto')
      return
    }

    setRunsById(mergeRunCollection({}, readStoredRuns(chatId)))
    setSelectedRunId(null)
    setSelectionMode('auto')
  }, [chatId, enabled])

  useEffect(() => {
    if (!enabled || !chatId || seedRuns.length === 0) return

    setRunsById((previous) => mergeRunCollection(previous, seedRuns))
  }, [chatId, enabled, seedRuns])

  useEffect(() => {
    if (!enabled || !chatId || typeof window === 'undefined') return

    try {
      sessionStorage.setItem(
        getStorageKey(chatId),
        JSON.stringify(Object.values(pruneRuns(runsById)).sort(sortRuns))
      )
    } catch {}
  }, [chatId, enabled, runsById])

  const runs = useMemo(
    () => Object.values(runsById).sort(sortRuns),
    [runsById]
  )

  const latestRun = runs[0] ?? null

  useEffect(() => {
    if (!latestRun) {
      if (selectedRunId !== null) {
        setSelectedRunId(null)
      }
      return
    }

    if (selectionMode === 'auto') {
      if (selectedRunId !== latestRun.runId) {
        setSelectedRunId(latestRun.runId)
      }
      return
    }

    if (selectedRunId && !runs.some((run) => run.runId === selectedRunId)) {
      setSelectedRunId(latestRun.runId)
    }
  }, [latestRun, runs, selectedRunId, selectionMode])

  const currentRun = useMemo(() => {
    if (!selectedRunId) return latestRun
    return runs.find((run) => run.runId === selectedRunId) ?? latestRun
  }, [latestRun, runs, selectedRunId])

  const selectRun = useCallback(
    (runId: string, options?: { manual?: boolean }) => {
      setSelectedRunId(runId)
      setSelectionMode(options?.manual === false ? 'auto' : 'manual')
    },
    []
  )

  useEffect(() => {
    if (!enabled || !chatId) return

    const handleToolStart = (event: {
      data?: {
        toolName?: string
        toolId?: string
        chatId?: string | null
        request?: unknown
      }
    }) => {
      const data = event.data
      const eventChatId = getScopedEventChatId(data?.chatId)

      if (
        !data ||
        eventChatId !== chatId ||
        data.toolName !== 'Sidecar__delegate' ||
        typeof data.toolId !== 'string' ||
        !data.toolId.trim() ||
        !isRealSidecarRunId(data.toolId)
      ) {
        return
      }

      const objective = readStartObjective(data.request)
      const runId = data.toolId

      setRunsById((previous) => {
        const existing = previous[runId]
        const seededRun =
          existing ??
          {
            ...createPlaceholderRunState(eventChatId, runId),
            objective,
            message: 'Starting Sidecar…'
          }

        return pruneRuns({
          ...previous,
          [runId]: {
            ...seededRun,
            objective: objective ?? seededRun.objective,
            message: seededRun.message ?? 'Starting Sidecar…'
          }
        })
      })
    }

    const handleRun = (event: { data?: SidecarRunEventData }) => {
      const data = event.data
      const eventChatId = getScopedEventChatId(data?.chatId)

      if (!data || eventChatId !== chatId || !isRealSidecarRunId(data.runId)) {
        return
      }

      setRunsById((previous) => {
        const base =
          previous[data.runId] ??
          createRunStateFromRunEvent({
            ...data,
            chatId: eventChatId
          })

        return pruneRuns({
          ...previous,
          [data.runId]: mergeRunEvent(base, {
            ...data,
            chatId: eventChatId
          })
        })
      })
    }

    const handleAgent = (event: { data?: SidecarAgentEventData }) => {
      const data = event.data
      const eventChatId = getScopedEventChatId(data?.chatId)

      if (!data || eventChatId !== chatId) return

      setRunsById((previous) => {
        const run =
          previous[data.runId] ??
          createRunStateFromAgentEvent({
            ...data,
            chatId: eventChatId
          })

        return pruneRuns({
          ...previous,
          [data.runId]: upsertAgent(run, {
            ...data,
            chatId: eventChatId
          })
        })
      })
    }

    const handleTool = (event: { data?: SidecarAgentToolEventData }) => {
      const data = event.data
      const eventChatId = getScopedEventChatId(data?.chatId)

      if (!data || eventChatId !== chatId) return

      setRunsById((previous) => {
        const run =
          previous[data.runId] ??
          createPlaceholderRunState(eventChatId, data.runId)
        const hydratedRun = run.agents[data.agentId]
          ? run
          : {
              ...run,
              agentOrder: run.agentOrder.includes(data.agentId)
                ? run.agentOrder
                : [...run.agentOrder, data.agentId],
              agents: {
                ...run.agents,
                [data.agentId]: createPlaceholderAgentState(data)
              }
            }

        return pruneRuns({
          ...previous,
          [data.runId]: upsertToolTimeline(hydratedRun, data)
        })
      })
    }

    const handleAnnotation = (event: { data?: SidecarAgentAnnotationEventData }) => {
      const data = event.data
      const eventChatId = getScopedEventChatId(data?.chatId)

      if (!data || eventChatId !== chatId || !isRealSidecarRunId(data.runId)) {
        return
      }

      setRunsById((previous) => {
        const run =
          previous[data.runId] ??
          createPlaceholderRunState(eventChatId, data.runId)
        const hydratedRun = run.agents[data.agentId]
          ? run
          : {
              ...run,
              agentOrder: run.agentOrder.includes(data.agentId)
                ? run.agentOrder
                : [...run.agentOrder, data.agentId],
              agents: {
                ...run.agents,
                [data.agentId]: createPlaceholderAgentState(data)
              }
            }

        return pruneRuns({
          ...previous,
          [data.runId]: mergeAnnotations(hydratedRun, data)
        })
      })
    }

    const handleStreamAnnotation = (event: {
      data?: { annotation?: Record<string, unknown>; chatId?: string | null; timestamp?: number }
    }) => {
      const data = event.data
      const eventChatId = getScopedEventChatId(data?.chatId)

      if (!data || eventChatId !== chatId) return

      const seededRun = buildSeedRunFromAnnotation({
        annotation: data.annotation,
        chatId: eventChatId,
        createdAt: data.timestamp ?? Date.now()
      })

      if (!seededRun) return

      setRunsById((previous) =>
        mergeRunCollection(previous, [seededRun])
      )
    }

    addEventListener('tool.start', handleToolStart)
    addEventListener('sidecar.run', handleRun)
    addEventListener('sidecar.agent', handleAgent)
    addEventListener('sidecar.agent.tool', handleTool)
    addEventListener('sidecar.agent.annotation', handleAnnotation)
    addEventListener('stream.annotation', handleStreamAnnotation)

    return () => {
      removeEventListener('tool.start', handleToolStart)
      removeEventListener('sidecar.run', handleRun)
      removeEventListener('sidecar.agent', handleAgent)
      removeEventListener('sidecar.agent.tool', handleTool)
      removeEventListener('sidecar.agent.annotation', handleAnnotation)
      removeEventListener('stream.annotation', handleStreamAnnotation)
    }
  }, [enabled, chatId, addEventListener, removeEventListener])

  const clearRun = useCallback(() => {
    setRunsById({})
    setSelectedRunId(null)
    setSelectionMode('auto')

    if (chatId && typeof window !== 'undefined') {
      try {
        sessionStorage.removeItem(getStorageKey(chatId))
      } catch {}
    }
  }, [chatId])

  const value = useMemo(
    () => ({
      currentRun,
      latestRun,
      runs,
      selectRun,
      clearRun
    }),
    [clearRun, currentRun, latestRun, runs, selectRun]
  )

  return value
}
